import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  Star as StarIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Calculate as CalculateIcon,
  Vaccines as VaccinesIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as HospitalIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { favoritesManager, FavoriteItem } from '../utils/favorites';

interface FavoritesPanelProps {
  userId: string;
  onItemClick?: (item: FavoriteItem) => void;
  onClose?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// function TabPanel(props: TabPanelProps) {
//   const { children, value, index, ...other } = props;
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`favorites-tabpanel-${index}`}
//       aria-labelledby={`favorites-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
//     </div>
//   );
// }

const FavoritesPanel: React.FC<FavoritesPanelProps> = ({ userId, onItemClick, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [filteredFavorites, setFilteredFavorites] = useState<FavoriteItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<FavoriteItem | null>(null);

  useEffect(() => {
    loadFavorites();
  }, [userId, tabValue]);

  const loadFavorites = () => {
    const allFavorites = favoritesManager.getUserFavorites(userId);
    setFavorites(allFavorites);
    setFilteredFavorites(allFavorites);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    loadFavorites();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      const searchResults = favoritesManager.searchFavorites(userId, query);
      setFilteredFavorites(searchResults);
    } else {
      setFilteredFavorites(favorites);
    }
  };

  const handleItemClick = (item: FavoriteItem) => {
    favoritesManager.markAsUsed(item.id);
    onItemClick?.(item);
  };

  const handleDeleteItem = (itemId: string) => {
    favoritesManager.removeFromFavorites(itemId);
    loadFavorites();
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: FavoriteItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const getTypeIcon = (type: FavoriteItem['type']) => {
    switch (type) {
      case 'medication':
        return <CalculateIcon sx={{ color: '#4A90E2' }} />;
      case 'vaccine':
        return <VaccinesIcon sx={{ color: '#50C878' }} />;
      case 'disease':
        return <PhotoCameraIcon sx={{ color: '#FF6B6B' }} />;
      case 'algorithm':
        return <HospitalIcon sx={{ color: '#FF9800' }} />;
      case 'protocol':
        return <DescriptionIcon sx={{ color: '#9C27B0' }} />;
      case 'calculation':
        return <CalculateIcon sx={{ color: '#607D8B' }} />;
      default:
        return <StarIcon sx={{ color: '#666' }} />;
    }
  };

  const getTypeLabel = (type: FavoriteItem['type']) => {
    switch (type) {
      case 'medication':
        return 'Препарат';
      case 'vaccine':
        return 'Вакцина';
      case 'disease':
        return 'Заболевание';
      case 'algorithm':
        return 'Алгоритм';
      case 'protocol':
        return 'Протокол';
      case 'calculation':
        return 'Расчет';
      default:
        return 'Элемент';
    }
  };

  // const formatDate = (date: Date) => {
  //   return date.toLocaleDateString('ru-RU');
  // };

  const getPopularFavorites = () => {
    return favoritesManager.getPopularFavorites(userId, 10);
  };

  const getRecentFavorites = () => {
    return favoritesManager.getRecentFavorites(userId, 10);
  };

  const getFavoritesByCategory = () => {
    return favoritesManager.getFavoritesByCategory(userId);
  };

  const currentItems = (() => {
    switch (tabValue) {
      case 0:
        return filteredFavorites;
      case 1:
        return getPopularFavorites();
      case 2:
        return getRecentFavorites();
      case 3:
        return Object.values(getFavoritesByCategory()).flat();
      default:
        return filteredFavorites;
    }
  })();

  return (
    <Card sx={{ 
      height: '100%',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
    }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Заголовок */}
        <Box sx={{ 
          p: 3, 
          borderBottom: 1, 
          borderColor: 'divider',
          background: 'linear-gradient(45deg, #FFD700, #FFA500)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Избранное
              </Typography>
            </Box>
            {onClose && (
              <Button onClick={onClose} sx={{ color: 'white' }}>
                Закрыть
              </Button>
            )}
          </Box>
        </Box>

        {/* Поиск */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Поиск в избранном..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#FFD700' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white'
              }
            }}
          />
        </Box>

        {/* Табы */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Все</Typography>
                  <Badge badgeContent={favorites.length} color="primary" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Популярные</Typography>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Недавние</Typography>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>По категориям</Typography>
                </Box>
              } 
            />
          </Tabs>
        </Box>

        {/* Список избранного */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {currentItems.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {tabValue === 0 ? 'Избранное пусто' : 'Нет элементов для отображения'}
            </Alert>
          ) : (
            <List>
              {currentItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#FFF8E1',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    <ListItemIcon>
                      {getTypeIcon(item.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Chip
                            label={getTypeLabel(item.type)}
                            size="small"
                            sx={{ 
                              backgroundColor: '#FFF8E1', 
                              color: '#FF8F00',
                              fontWeight: 500
                            }}
                          />
                          {item.usageCount > 0 && (
                            <Chip
                              label={`${item.usageCount} раз`}
                              size="small"
                              sx={{ 
                                backgroundColor: '#E8F5E8', 
                                color: '#50C878',
                                fontWeight: 500
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                            {item.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#999' }}>
                              {item.category}
                            </Typography>
                            {item.tags.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {item.tags.slice(0, 3).map((tag, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tag}
                                    size="small"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.7rem',
                                      backgroundColor: '#F0F0F0',
                                      color: '#666'
                                    }}
                                  />
                                ))}
                                {item.tags.length > 3 && (
                                  <Typography variant="caption" sx={{ color: '#999' }}>
                                    +{item.tags.length - 3}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, item);
                        }}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < currentItems.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Контекстное меню */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleDeleteItem(selectedItem?.id || '')}>
            <DeleteIcon sx={{ mr: 1, color: '#F44336' }} />
            Удалить из избранного
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

export default FavoritesPanel;
